import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, ArrowRight, Loader2, CheckCircle, Building2, User } from "lucide-react";
import api from "../../../services/api";

const ContentUpload = () => {
  const navigate = useNavigate();
  const [contentFile, setContentFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [parseResult, setParseResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [websiteType, setWebsiteType] = useState('business');

  // Fetch website type on mount
  useState(() => {
    const fetchType = async () => {
      try {
        const res = await api.get('/templates/user/website-type/');
        setWebsiteType(res.data.website_type || 'business');
      } catch (err) {
        console.error('Error fetching website type:', err);
      }
    };
    fetchType();
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const SUPPORTED_FORMATS = ['.pdf', '.docx', '.txt'];

  const validateFile = (file) => {
    const fileName = file.name.toLowerCase();
    const extension = '.' + fileName.split('.').pop();

    if (!SUPPORTED_FORMATS.includes(extension)) {
      if (extension === '.doc') {
        return {
          valid: false,
          error: "The .doc format is not supported. Please save your document as .docx (Word 2007+) or PDF and try again."
        };
      }
      return {
        valid: false,
        error: `Unsupported file format (${extension}). Please upload a PDF, DOCX, or TXT file.`
      };
    }

    if (file.size > 10 * 1024 * 1024) {
      return {
        valid: false,
        error: "File is too large. Maximum size is 10MB."
      };
    }

    return { valid: true, error: null };
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validation = validateFile(file);

      if (!validation.valid) {
        setMessage(validation.error);
        setContentFile(null);
        return;
      }

      setMessage("");
      setContentFile(file);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validation = validateFile(file);

      if (!validation.valid) {
        setMessage(validation.error);
        setContentFile(null);
        return;
      }

      setMessage("");
      setContentFile(file);
    }
  };

  const handleUpload = async () => {
    if (!contentFile) {
      setMessage("Please select a document first");
      return;
    }

    setIsUploading(true);
    setMessage("");
    setParseResult(null);

    try {
      const formData = new FormData();
      formData.append("document", contentFile);
      formData.append("website_type", websiteType);

      const response = await api.post("/profile/parse-business-content/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;

      // Handle extraction failure - stay on page
      if (data.message?.includes("Could not extract") || data.message?.includes("empty")) {
        setMessage("Could not extract text from document. Please ensure the file contains readable text and try again, or upload a different file.");
        setContentFile(null);
        return;
      }

      // Success
      setMessage(data.message || "Content parsed successfully!");

      if (data.parsed_data) {
        setParseResult(data.parsed_data);
        setTimeout(() => {
          navigate("/dashboard/onboarding/select-template");
        }, 3000);
      } else {
        setTimeout(() => {
          navigate("/dashboard/onboarding/select-template");
        }, 1500);
      }
    } catch (error) {
      console.error("Error uploading content:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error;
      if (errorMsg?.includes("extract") || errorMsg?.includes("empty")) {
        setMessage("Could not extract text from document. Please try a different file format (PDF or DOCX recommended).");
      } else {
        setMessage("Failed to parse content. Please try again or skip.");
      }
      setContentFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkip = () => {
    navigate("/dashboard/onboarding/select-template");
  };

  const isFreelancer = websiteType === 'freelancer';
  const Icon = isFreelancer ? User : Building2;
  const title = isFreelancer ? "Tell us about your work" : "Tell us about your business";
  const subtitle = isFreelancer
    ? "Upload a document describing your services, portfolio, and expertise"
    : "Upload a document describing your company, services, and offerings";

  const sampleHeadings = isFreelancer
    ? ["Home/Intro", "About Me", "Services", "Skills", "Testimonials", "Pricing"]
    : ["Home/Intro", "About Us", "Services", "Why Choose Us", "Team", "FAQ", "Locations"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 mb-4">
            <Icon className="text-white" size={32} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
          <p className="text-gray-300">{subtitle}</p>
        </div>

        {/* Upload Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          {/* Sample format hint */}
          <div className="mb-6 p-4 rounded-xl bg-indigo-500/20 border border-indigo-400/30">
            <h3 className="text-white font-medium mb-2 text-sm">Suggested Document Format:</h3>
            <p className="text-gray-300 text-xs mb-2">Include sections with clear headings like:</p>
            <div className="flex flex-wrap gap-2">
              {sampleHeadings.map((heading) => (
                <span key={heading} className="text-xs px-2 py-1 rounded-full bg-indigo-500/30 text-indigo-200">
                  {heading}
                </span>
              ))}
            </div>
          </div>

          {/* Drag & Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              dragActive
                ? "border-purple-400 bg-purple-500/20"
                : contentFile
                ? "border-green-400 bg-green-500/10"
                : "border-gray-500 hover:border-purple-400"
            }`}
          >
            <input
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            {contentFile ? (
              <div className="flex flex-col items-center">
                <CheckCircle className="text-green-400 mb-3" size={48} />
                <p className="text-white font-medium">{contentFile.name}</p>
                <p className="text-gray-400 text-sm mt-1">
                  {(contentFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="text-gray-400 mb-3" size={48} />
                <p className="text-white font-medium">Drag & drop your content document</p>
                <p className="text-gray-400 text-sm mt-1">or click to browse</p>
                <p className="text-gray-500 text-xs mt-3">PDF, DOCX, TXT (Max 10MB)</p>
              </div>
            )}
          </div>

          {/* Message */}
          {message && (
            <div className={`mt-4 p-3 rounded-lg text-center text-sm ${
              message.includes("Failed") || message.includes("error") || message.includes("not supported") || message.includes("Could not") || message.includes("Unsupported")
                ? "bg-red-500/20 text-red-300 border border-red-500/30"
                : "bg-green-500/20 text-green-300"
            }`}>
              {message}
            </div>
          )}

          {/* Parse Results */}
          {parseResult && (
            <div className="mt-4 p-4 rounded-lg bg-indigo-500/20 border border-indigo-400/30">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                Content Imported
              </h3>
              <div className="space-y-2 text-sm">
                {parseResult.about && (
                  <p className="text-gray-300"><span className="text-gray-400">About:</span> Imported</p>
                )}
                {parseResult.services?.length > 0 && (
                  <p className="text-gray-300"><span className="text-gray-400">Services:</span> {parseResult.services.length} items</p>
                )}
                {parseResult.team?.length > 0 && (
                  <p className="text-gray-300"><span className="text-gray-400">Team:</span> {parseResult.team.length} members</p>
                )}
                {parseResult.faq?.length > 0 && (
                  <p className="text-gray-300"><span className="text-gray-400">FAQ:</span> {parseResult.faq.length} questions</p>
                )}
                {parseResult.testimonials?.length > 0 && (
                  <p className="text-gray-300"><span className="text-gray-400">Testimonials:</span> {parseResult.testimonials.length} reviews</p>
                )}
              </div>
              <p className="text-indigo-300 text-xs mt-3">Redirecting to template selection...</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={handleSkip}
              className="flex-1 px-6 py-3 rounded-xl text-gray-300 border border-gray-600 hover:bg-white/5 transition-all font-medium"
            >
              Skip for now
            </button>
            <button
              onClick={handleUpload}
              disabled={!contentFile || isUploading}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Processing...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-center">
          <div className="text-gray-400 text-sm">
            <div className="text-2xl mb-1">AI</div>
            Auto-fill sections
          </div>
          <div className="text-gray-400 text-sm">
            <div className="text-2xl mb-1">5x</div>
            Faster setup
          </div>
          <div className="text-gray-400 text-sm">
            <div className="text-2xl mb-1">100%</div>
            Editable
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentUpload;
